import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import useAppDispatch from '@/hooks/useAppDispatch'
import { REFERRAL_SOURCE } from '@/lib/constants'
import { isNotProduction } from '@/lib/helpers/envHelpers'
import {
  getErrorDebugInfo,
  isConstraintViolationListError,
} from '@/lib/helpers/helperFunctions'
import { buildCreateHistoricalReferralRequest } from '@/lib/helpers/referralImportHelpers'
import clientService from '@/lib/services/clientService'
import constraintViolationService from '@/lib/services/constraintViolationService'
import referralService from '@/lib/services/referralService'
import { ReferralsImportItem } from '@/redux/slices/referralsImportBase'
import {
  updateImportItem,
  updateImportItemStatus,
} from '@/redux/slices/referralsImportHistorical'
import { FormErrors } from '@/types/misc'
import { CreateClientForReferralRequest } from '@/types/requests/clients'

export function useImportHistoricalReferralMutation() {
  const dispatch = useAppDispatch()

  return useMutation({
    // retry mutation with linear backoff delay (for 4 retries the last delay is 3000 ms, total time 7.5 s)
    retry: import.meta.env.VITE_REFERRAL_MAX_RETRIES,
    retryDelay: (attempt) => attempt * 750,
    mutationFn: async (importItem: ReferralsImportItem) => {
      if (!importItem.importingCompany) {
        // do not import when importingCompany is missing
        return
      }
      dispatch(
        updateImportItemStatus({
          refUUID: importItem.refUUID,
          importStatus: 'processing',
        }),
      )

      // create a user client if missing
      const newClientEmail = importItem.sourceClientEmail

      // - importingCompanyClient is needed in all cases
      let importingCompanyClient = importItem.importingCompanyClient

      if (importingCompanyClient === null) {
        // create a client if missing
        importingCompanyClient =
          await clientService.createClientForReferralOrFindExisting(
            importItem.importingCompany.id,
            buildCreateClientRequest(
              importItem.sourceClientFirstName,
              importItem.sourceClientLastName,
              newClientEmail,
              importItem.sourceClientTelephone,
            ),
          )

        // update import data state
        dispatch(
          updateImportItem({
            refUUID: importItem.refUUID,
            importingCompanyClient,
          }),
        )
      } else {
        // eventually update existing client
        if (importItem.onClientDuplicate === 'update') {
          await clientService.updateClientForReferral(
            importingCompanyClient.id,
            buildCreateClientRequest(
              importItem.sourceClientFirstName,
              importItem.sourceClientLastName,
              importItem.sourceClientEmail,
              importItem.sourceClientTelephone,
            ),
          )
        }
      }

      // import with resolved client
      const request = buildCreateHistoricalReferralRequest({
        ...importItem,
        source: REFERRAL_SOURCE.DIRECT,
        isLoggedReferral: false,
        importingCompanyClient: importingCompanyClient,
      })

      return referralService.createReferral(request)
    },
    onError: async (error, importItem) => {
      let formattedErrors: FormErrors | undefined

      if (isConstraintViolationListError(error)) {
        formattedErrors = constraintViolationService.formatErrors(error)
      } else {
        if (error instanceof AxiosError) {
          formattedErrors = {
            error: error.response?.data?.detail ?? error.message,
          }
        } else if (error.message) {
          formattedErrors = {
            error: error.message.replace('Invariant failed: ', ''),
          }
        } else {
          formattedErrors = {
            error: 'An error occurred',
          }
        }

        if (isNotProduction()) {
          formattedErrors.debug = JSON.stringify(getErrorDebugInfo(error))
        }
      }

      dispatch(
        updateImportItemStatus({
          refUUID: importItem.refUUID,
          importStatus: 'error',
          importErrors: formattedErrors,
        }),
      )
    },
    onSuccess: async (_data, importItem) => {
      dispatch(
        updateImportItemStatus({
          refUUID: importItem.refUUID,
          importStatus: 'imported',
          importErrors: undefined,
        }),
      )
    },
  })
}

function buildCreateClientRequest(
  firstName: string,
  lastName: string,
  email: string,
  telephone: string,
): CreateClientForReferralRequest {
  return {
    firstName: firstName,
    lastName: lastName,
    email: email,
    telephone: telephone,
    clientId: '',
  }
}
